import { FC } from 'react';
import { twMerge } from 'tailwind-merge';

import { ChevronDownIcon } from '../../../../assets/icons/index';
import { Radio, RadioGroup } from '../../../../components/radio-group/index';
import {
  Select,
  SelectButton,
  SelectOption,
  SelectOptions,
} from '../../../../components/select/index';

export type VestingGraphFilterOption = {
  label: string;
  value: string;
};

export enum VestingGraphFilterVariant {
  DROPDOWN = 'dropdown',
  HIDDEN = 'hidden',
  SWITCH = 'switch',
}

export const resolveVestingGraphFilterVariant = (optionCount: number) => {
  if (optionCount <= 1) return VestingGraphFilterVariant.HIDDEN;
  if (optionCount <= 3) return VestingGraphFilterVariant.SWITCH;

  return VestingGraphFilterVariant.DROPDOWN;
};

type VestingGraphFilterProps = {
  onChange: (value: string) => void;
  options: VestingGraphFilterOption[];
  value: string;
};

export const VestingGraphFilter: FC<VestingGraphFilterProps> = ({ onChange, options, value }) => {
  const variant = resolveVestingGraphFilterVariant(options.length);
  const selected = options.find((option) => option.value === value) ?? options[0];

  if (variant === VestingGraphFilterVariant.HIDDEN || !selected) return null;

  if (variant === VestingGraphFilterVariant.SWITCH) {
    return (
      <RadioGroup
        className="divide-gray-200"
        onChange={onChange}
        value={value}
        wrapperClassName="h-fit w-fit shrink-0 rounded-[4px] border border-gray-200"
      >
        {options.map((option) => (
          <Radio
            className="h-[34px] whitespace-nowrap bg-white px-4 text-sm font-[450] text-[#858593] data-[checked]:bg-brand-25 data-[checked]:font-[550] data-[checked]:text-[#172335]"
            key={option.value}
            styleType="NONE"
            value={option.value}
          >
            {option.label}
          </Radio>
        ))}
      </RadioGroup>
    );
  }

  return (
    <Select onChange={onChange} value={value}>
      {({ open }) => (
        <div className="relative shrink-0">
          <SelectButton className="flex h-auto items-center gap-2 rounded border border-gray-200 bg-gray-50 px-3 py-2 text-sm font-[450] text-gray-700">
            <span className="truncate">{selected.label}</span>
            <ChevronDownIcon
              className={twMerge(
                'h-4 w-4 text-gray-400 transition-transform duration-200',
                open && 'rotate-180',
              )}
            />
          </SelectButton>
          <SelectOptions
            anchor={{ gap: 4, to: 'bottom end' }}
            className="z-20 max-h-[400px] w-[var(--button-width)] flex-col gap-2 overflow-y-auto text-nowrap rounded bg-white p-2 shadow-xl ring-0"
          >
            {options.map((option) => (
              <SelectOption
                className="h-auto w-full rounded px-2 py-[6px]"
                key={option.value}
                value={option.value}
              >
                <span className="block w-full truncate text-sm text-gray-700">{option.label}</span>
              </SelectOption>
            ))}
          </SelectOptions>
        </div>
      )}
    </Select>
  );
};
